import React from 'react';
import { reportError } from '../../utils/errorReporter.js';
import { isChunkLoadError, tryAutoReload } from '../../utils/chunkReload.js';

// A chunk failure that a reload cannot fix must not become a reload loop. The
// capped auto-reload lives in src/utils/chunkReload.js, shared with main.jsx's
// global listeners so both paths spend ONE budget (GH #7430): at most
// MAX_AUTO_RELOADS inside RELOAD_WINDOW_MS, then the error banner below, which
// names the failure and offers a manual Reload.

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Two failures reach here as a chunk error. A stale chunk after a new
    // deployment (the browser cached the old JS entrypoint but the hashed files
    // it names are gone), and a chunk fetch that stalled long enough for
    // lazyWithTimeout to give up on it. A hard reload fixes the first outright
    // and is the only recovery available for the second, so try it either way —
    // but only while tryAutoReload's cap allows, or a persistent stall would
    // reload the app forever. Cap spent: fall through to the visible banner.
    const msg = error?.message || '';
    if (isChunkLoadError(msg) && tryAutoReload()) {
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    // Fire-and-forget — never await; never block the UI or re-throw.
    reportError({
      message: error?.message || String(error),
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      context: 'ErrorBoundary',
    });
  }

  handleDismiss = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 99999,
              padding: '14px 24px',
              background: '#991b1b',
              color: '#fecaca',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: 13,
              boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>ERROR</span>
              <span>{this.state.error?.message || 'Something went wrong'}</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={this.handleDismiss}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: '#fff',
                  padding: '5px 14px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontFamily: 'inherit',
                }}
              >
                Dismiss
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: '#fff',
                  padding: '5px 14px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontFamily: 'inherit',
                }}
              >
                Reload
              </button>
            </div>
          </div>
          {this.props.children}
        </>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
